import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { isNotEmpty, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { initialSupplier, Supplier } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { createSupplier, updateSupplier } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import axios from 'axios'
import { SuppliersListLoading } from '../components/loading/SuppliersListLoading'

type Props = {
  isSupplierLoading: boolean
  supplier: Supplier
}

const editSupplierSchema = Yup.object().shape({
  supplier_name: Yup.string().required('Supplier name is required'),
  contact_details: Yup.string().nullable(),
  address: Yup.string().nullable(),
  status: Yup.boolean().required('Status is required'),
})

const SupplierEditModalForm: FC<Props> = ({ supplier, isSupplierLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL
  const intl = useIntl()

  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [supplierForEdit] = useState<Supplier>({
    ...supplier,
    supplier_name: supplier.supplier_name || initialSupplier.supplier_name,
    contact_details: supplier.contact_details || initialSupplier.contact_details,
    address: supplier.address || initialSupplier.address,
    status: supplier.status ?? initialSupplier.status,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: supplierForEdit,
    validationSchema: editSupplierSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateSupplier(values)
        } else {
          await createSupplier(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  return (
    <>
      <form id='kt_modal_add_supplier_form' encType="multipart/form-data" className='form' onSubmit={formik.handleSubmit} noValidate>
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_supplier_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_supplier_header'
          data-kt-scroll-wrappers='#kt_modal_add_supplier_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'>
                <FormattedMessage id='SUPPLIER.FORM.INPUT.SUPPLIERNAME' defaultMessage='Supplier Name' />
              </label>
              <input
                placeholder={intl.formatMessage({ id: 'SUPPLIER.FORM.PLACEHOLDER.SUPPLIERNAME', defaultMessage: 'Enter supplier name' })}
                {...formik.getFieldProps('supplier_name')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.supplier_name && formik.errors.supplier_name },
                  { 'is-valid': formik.touched.supplier_name && !formik.errors.supplier_name }
                )}
                type='text'
                autoComplete='off'
                disabled={formik.isSubmitting || isSupplierLoading}
              />
              {formik.touched.supplier_name && formik.errors.supplier_name && (
                <div className='fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.supplier_name}</span>
                </div>
              )}
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>
                <FormattedMessage id='SUPPLIER.FORM.INPUT.CONTACTDETAILS' defaultMessage='Contact Details' />
              </label>
              <input
                {...formik.getFieldProps('contact_details')}
                className='form-control form-control-solid'
                type='text'
                autoComplete='off'
                placeholder={intl.formatMessage({ id: 'SUPPLIER.FORM.PLACEHOLDER.CONTACTDETAILS', defaultMessage: 'Enter contact details' })}
                disabled={formik.isSubmitting || isSupplierLoading}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>
                <FormattedMessage id='SUPPLIER.FORM.INPUT.ADDRESS' defaultMessage='Address' />
              </label>
              <input
                {...formik.getFieldProps('address')}
                className='form-control form-control-solid'
                type='text'
                autoComplete='off'
                placeholder={intl.formatMessage({ id: 'SUPPLIER.FORM.PLACEHOLDER.ADDRESS', defaultMessage: 'Enter address' })}
                disabled={formik.isSubmitting || isSupplierLoading}
              />
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>
                <FormattedMessage id='SUPPLIER.FORM.INPUT.STATUS' defaultMessage='Status' />
              </label>
              <select
                {...formik.getFieldProps('status')}
                className='form-select form-control'
                disabled={formik.isSubmitting || isSupplierLoading}
              >
                <option value='1'>{intl.formatMessage({ id: 'FORM.GENERAL.ACTIVE', defaultMessage: 'Active' })}</option>
                <option value='0'>{intl.formatMessage({ id: 'FORM.GENERAL.INACTIVE', defaultMessage: 'Inactive' })}</option>
              </select>
            </div>
          </div>
        </div>

        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-suppliers-modal-action='cancel'
            disabled={formik.isSubmitting || isSupplierLoading}
          >
            <FormattedMessage id='FORM.GENERAL.DISCARD' defaultMessage='Discard' />
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-suppliers-modal-action='submit'
            disabled={isSupplierLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'><FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='Submit' /></span>
            {(formik.isSubmitting || isSupplierLoading) && (
              <span className='indicator-progress'>
                <FormattedMessage id='FORM.GENERAL.PLEASEWAIT' defaultMessage='Please wait...' />
                {' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isSupplierLoading) && <SuppliersListLoading />}
    </>
  )
}

export { SupplierEditModalForm }
